/**
 * Grouped choices to add to the conversation draft. No notion of days or slots here —
 * `ChoiceColumn`/item vocabulary is deliberately abstract so any
 * grouped-choice UI (not just scheduling) can reuse it.
 */
import type { FC } from 'react';
import { Button } from '../../shadcdn/button.tsx';
import { Check, Plus } from 'lucide-react';

export interface ChoiceColumnItem {
  id: string;
  label: string;
  caption?: string;
  disabled?: boolean;
}

export interface ChoiceColumn {
  id: string;
  title: string;
  caption?: string;
  highlight?: boolean;
  items: ChoiceColumnItem[];
}

export interface ChoiceBoardProps {
  columns: ChoiceColumn[];
  selectedId?: string;
  onPick: (columnId: string, itemId: string) => void;
}

export const ChoiceBoard: FC<ChoiceBoardProps> = ({ columns, selectedId, onPick }) => (
  <div className="grid grid-cols-1 gap-3.5 md:grid-cols-3">
    {columns.map((column) => (
      <div key={column.id} className="min-w-0 py-2" data-highlight={column.highlight === true}>
        <div className="mb-2.5 flex items-baseline justify-between border-b border-accent pb-2 text-sm font-bold">
          <span>{column.title}</span>
          {column.caption ? (
            <small className="text-xs font-medium text-muted-foreground-subtle">
              {column.caption}
            </small>
          ) : null}
        </div>
        <div className="flex flex-col gap-1.75">
          {column.items.map((item) => (
            <Button
              key={item.id}
              type="button"
              variant="suggestion"
              size="surface"
              className="w-full tabular-nums data-[selected=true]:underline"
              data-selected={item.id === selectedId}
              data-disabled={item.disabled === true}
              disabled={item.disabled === true}
              onClick={() => onPick(column.id, item.id)}
            >
              {item.id === selectedId ? (
                <Check className="me-2 h-4 w-4" aria-hidden="true" />
              ) : (
                <Plus className="me-2 h-4 w-4" aria-hidden="true" />
              )}
              <span>{item.label}</span>
              {item.caption ? (
                <small className="text-xs font-medium text-muted-foreground-subtle">
                  {item.caption}
                </small>
              ) : null}
            </Button>
          ))}
        </div>
      </div>
    ))}
  </div>
);
